import {useSelector} from "react-redux";
import {useEffect, useRef, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import Tabs from "@/components/Tabs";
import CompetitionEditionCalendar from "@/components/CompetitionEditionCalendar";
import VLFModal from "@/components/Modal";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import MatchSportingConfig, {
	editionSportingDefaults,
	sportingSummary,
	sportingPayload,
	sportingPeriodsError,
	sportingStructureLocked,
} from "@/components/MatchSportingConfig";
import VLFSelectNew from "@/components/Input/SelectNew";
import {Button, Checkbox, FormControlLabel} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import RemoveIcon from "@mui/icons-material/Remove";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import EventIcon from "@mui/icons-material/Event";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {CompetitionSeasonAPI} from "@/api/competitionSeason";
import {ClubAPI} from "@/api/club";
import {RosterAPI} from "@/api/roster";
import {MatchAPI} from "@/api/match";
import {getInternalClubId, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {AREAS, canWrite} from "@/utils/access";
import moment from "moment";
import "./index.scss";

const CompetitionAction = ({readOnly, ...props}) =>
	readOnly && props.title !== "Dettaglio" ? null : <Button {...props} />;

function ParticipantsNavigation({tabs, currentTab, setCurrentTab}) {
	const navigation = useRef(null);
	useEffect(() => {
		const keepSelectedVisible = () => {
			const container = navigation.current;
			const selected = container?.querySelector('[aria-pressed="true"]');
			if (!selected) return;
			const bounds = container.getBoundingClientRect();
			const active = selected.getBoundingClientRect();
			if (active.left < bounds.left)
				container.scrollLeft += active.left - bounds.left;
			else if (active.right > bounds.right)
				container.scrollLeft += active.right - bounds.right;
		};
		keepSelectedVisible();
		const observer = new ResizeObserver(keepSelectedVisible);
		observer.observe(navigation.current);
		return () => observer.disconnect();
	}, [currentTab]);
	return (
		<>
			<div
				className="cs-participants__navigation"
				role="group"
				aria-label="Gironi partecipanti"
				ref={navigation}
			>
				{tabs.map((item) => (
					<button
						type="button"
						key={item.key}
						aria-pressed={currentTab === item.key}
						onClick={() => setCurrentTab(item.key)}
					>
						{item.label}
					</button>
				))}
			</div>
			{tabs.find((item) => item.key === currentTab)?.render()}
		</>
	);
}

export default function CompetitionSeasonPage({handleNotification}) {
	const {id} = useParams();
	const navigate = useNavigate();
	const selectedSeasonId = useSelector((state) => state?.season?.id);
	const user = useSelector((state) => state?.user);
	const readOnly = !canWrite(user, AREAS.CONFIGURAZIONI);
	const listPath = "/competitions";
	const [cs, setCs] = useState(null),
		[loading, setLoading] = useState(true),
		[tab, setTab] = useState("info");
	const [calendarSelection, setCalendarSelection] = useState({});
	const [showEditionRules, setShowEditionRules] = useState(false);
	const [participantsTab, setParticipantsTab] = useState(null);
	const [clubs, setClubs] = useState([]),
		[rosters, setRosters] = useState([]),
		[modal, setModal] = useState(null),
		[fields, setFields] = useState({});
	const label = (p) =>
		p.clubName + (p.name && p.name !== p.clubName ? " — " + p.name : "");
	const refresh = async () => {
		setLoading(true);
		try {
			setCs(null);
			const data = readOnly
				? await CompetitionSeasonAPI.getForCurrentUser(id, {
						seasonId: selectedSeasonId,
					})
				: await CompetitionSeasonAPI.getById(id);
			setCs(data);
			setRosters(
				(await RosterAPI.list({seasonId: data.seasonId, prospect: false})) ||
					[],
			);
		} catch (e) {
			handleNotification(
				e.response?.data?.error || "Impossibile caricare l'edizione",
				"error",
			);
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		setCalendarSelection({});
		setShowEditionRules(false);
		setParticipantsTab(null);
		refresh();
		ClubAPI.list()
			.then((data) => setClubs(data || []))
			.catch(() => handleNotification("Impossibile caricare i club", "error"));
	}, [id, readOnly, selectedSeasonId]);
	const open = (type, data = {}) => {
		setFields({...data});
		setModal({type, ...data});
	};
	const action = async (fn) => {
		setLoading(true);
		try {
			await fn();
			setModal(null);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			await refresh();
		} catch (e) {
			handleNotification(
				e.response?.data?.error || "Operazione non riuscita: controlla i dati",
				"error",
			);
		} finally {
			setLoading(false);
		}
	};
	const rounds = (cs?.groups || []).flatMap((g) =>
		(g.rounds || []).map((r) => ({...r, groupName: g.name})),
	);
	const participants = cs?.participants || [];
	const participantGroups = (cs?.groups || [])
		.slice()
		.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
	const assignedParticipants = new Set(
		participantGroups.flatMap((g) => (g.participants || []).map((p) => p.id)),
	);
	const unassignedParticipants = participants.filter(
		(p) => !assignedParticipants.has(p.id),
	);
	const selectedParticipantsTab =
		participantsTab === "unassigned" ||
		participantGroups.some((g) => g.id === participantsTab)
			? participantsTab
			: unassignedParticipants.length || !participantGroups.length
				? "unassigned"
				: participantGroups[0].id;
	useEffect(() => {
		if (cs && participantsTab === null)
			setParticipantsTab(selectedParticipantsTab);
	}, [cs, participantsTab, selectedParticipantsTab]);

	const save = () =>
		action(async () => {
			switch (modal.type) {
				case "group":
					if (!fields.name?.trim()) throw Error();
					await (fields.id
						? CompetitionSeasonAPI.updateGroup(id, fields.id, {
								name: fields.name,
								sortOrder: Number(fields.sortOrder) || 0,
							})
						: CompetitionSeasonAPI.createGroup(id, {
								name: fields.name,
								sortOrder: Number(fields.sortOrder) || 0,
							}));
					break;
				case "round":
					if (!fields.name?.trim() || Number(fields.roundNumber) < 1)
						throw Error();
					await (fields.id
						? CompetitionSeasonAPI.updateRound(id, fields.groupId, fields.id, {
								name: fields.name,
								roundNumber: Number(fields.roundNumber),
								roundType: fields.roundType || "regular",
								startDate: fields.startDate || null,
								endDate: fields.endDate || null,
							})
						: CompetitionSeasonAPI.createRound(id, fields.groupId, {
								name: fields.name,
								roundNumber: Number(fields.roundNumber),
								roundType: fields.roundType || "regular",
								startDate: fields.startDate || null,
								endDate: fields.endDate || null,
							}));
					break;
				case "participant": {
					const body = {
						clubId: fields.club?.value,
						rosterId: fields.roster?.value || null,
						name:
							fields.name?.trim() || fields.roster?.label || fields.club?.label,
					};
					if (fields.id)
						await CompetitionSeasonAPI.updateParticipant(id, fields.id, body);
					else await CompetitionSeasonAPI.addParticipant(id, body);
					break;
				}
				case "link":
					if (
						!(fields.groupId || fields.group?.value) ||
						!fields.participant?.value
					)
						throw Error();
					await CompetitionSeasonAPI.linkParticipant(
						id,
						fields.groupId || fields.group?.value,
						fields.participant?.value,
					);
					break;
				case "assign":
					await CompetitionSeasonAPI.addMatchToRound(
						id,
						fields.round.groupId,
						fields.round.value,
						fields.matchId,
					);
					break;
				case "match": {
					const sportingError = sportingPeriodsError(fields.sportingPeriods);
					if (sportingError) throw new Error(sportingError);
					const home = participants.find((p) => p.id === fields.home?.value),
						away = participants.find((p) => p.id === fields.away?.value);
					if (
						!home ||
						!away ||
						home.id === away.id ||
						!fields.startDate ||
						Number(fields.duration) <= 0
					)
						throw Error();
					const body = {
						...fields.original,
						...sportingPayload(fields),
						id: fields.id,
						description: fields.original?.description || "",
						homeClubId: home.clubId,
						awayClubId: away.clubId,
						homeRosterId: home.rosterId,
						awayRosterId: away.rosterId,
						homeParticipantId: home.id,
						awayParticipantId: away.id,
						competitionSeasonId: id,
						competitionSeasonGroupRoundId: fields.roundId || null,
						seasonId: cs.seasonId,
						playersCategoryId: cs.playerCategoryId || null,
						startDate: moment(fields.startDate).format("YYYY-MM-DDTHH:mm:ss"),
						startTimeToBeConfirmed: !!fields.startTimeToBeConfirmed,
						endDate: moment(fields.startDate)
							.add(Number(fields.duration), "minutes")
							.format("YYYY-MM-DDTHH:mm:ss"),
						matchType: cs.competition?.competitionType,
						homeClubGoals:
							fields.homeGoals === "" ? null : Number(fields.homeGoals),
						awayClubGoals:
							fields.awayGoals === "" ? null : Number(fields.awayGoals),
					};
					await (fields.id
						? MatchAPI.update(fields.id, body)
						: MatchAPI.create(body));
					break;
				}
				default:
					break;
			}
		});
	const editMatch = async (m, roundId) => {
		try {
			const original = await MatchAPI.getById(m.id || m.matchId);
			open("match", {
				id: original.id,
				original,
				sportingPeriods: original.sportingPeriods,
				allowReentry: original.allowReentry,
				roundId,
				home: {
					value: original.homeParticipantId,
					label: label(
						participants.find((p) => p.id === original.homeParticipantId) || {
							clubName: original.homeClub,
						},
					),
				},
				away: {
					value: original.awayParticipantId,
					label: label(
						participants.find((p) => p.id === original.awayParticipantId) || {
							clubName: original.awayClub,
						},
					),
				},
				startDate: moment(original.startDate).format("YYYY-MM-DDTHH:mm"),
				startTimeToBeConfirmed: !!original.startTimeToBeConfirmed,
				duration: moment(original.endDate).diff(
					moment(original.startDate),
					"minutes",
				),
				homeGoals: original.homeClubGoals ?? "",
				awayGoals: original.awayClubGoals ?? "",
			});
		} catch {
			handleNotification("Impossibile caricare la partita", "error");
		}
	};
	const matchRow = (m, round) => {
		const data = m.match || m;
		const home = participants.find((p) => p.id === data.homeParticipantId);
		const away = participants.find((p) => p.id === data.awayParticipantId);
		const homeGoals = data.result ? data.result.home : data.homeClubGoals;
		const awayGoals = data.result ? data.result.away : data.awayClubGoals;
		const scored = homeGoals != null && awayGoals != null;
		const teamName = (p, fallback) => {
			const club = p?.clubName || fallback || "";
			const name = p?.name || "";
			return !name
				? club || "Squadra da indicare"
				: !club ||
					  name.toLocaleLowerCase() === club.toLocaleLowerCase() ||
					  name.toLocaleLowerCase().startsWith(club.toLocaleLowerCase() + " ")
					? name
					: club + " — " + name;
		};
		const own =
			data.homeClubId === getInternalClubId() ||
			data.awayClubId === getInternalClubId();
		return (
			<div
				className={`cs-match-row${own ? " cs-match-row--own" : ""}`}
				key={data.id}
			>
				<div className="cs-fixture-date">
					<strong>{moment(data.startDate).format("DD/MM")}</strong>
					<span
						title={
							data.startTimeToBeConfirmed ? "Orario da definire" : undefined
						}
					>
						{data.startTimeToBeConfirmed
							? "OdD"
							: moment(data.startDate).format("HH:mm")}
					</span>
				</div>
				<Link
					className="cs-fixture"
					to={"/matches/" + data.id}
					title="Apri partita"
				>
					<span className="cs-fixture-team cs-fixture-team--home">
						{teamName(home, data.homeClub)}
					</span>
					<span
						className={`cs-fixture-score${scored ? " cs-fixture-score--played" : ""}`}
					>
						{scored ? `${homeGoals} – ${awayGoals}` : "VS"}
					</span>
					<span className="cs-fixture-team cs-fixture-team--away">
						{teamName(away, data.awayClub)}
					</span>
				</Link>
				<div className="cs-actions">
					<CompetitionAction
						readOnly={readOnly}
						variant="outlined"
						size="small"
						className="cs-icon-action"
						title="Dettaglio"
						aria-label="Dettaglio"
						onClick={() => navigate("/matches/" + data.id)}
					>
						<VisibilityIcon fontSize="small" />
					</CompetitionAction>
					<CompetitionAction
						readOnly={readOnly}
						variant="outlined"
						size="small"
						className="cs-icon-action"
						title="Modifica partita"
						aria-label="Modifica partita"
						onClick={() => editMatch(data, round?.id)}
					>
						<EditIcon fontSize="small" />
					</CompetitionAction>
					{round ? (
						<>
							<CompetitionAction
								readOnly={readOnly}
								variant="outlined"
								size="small"
								className="cs-icon-action"
								title="Da assegnare"
								aria-label="Da assegnare"
								onClick={() =>
									action(() =>
										CompetitionSeasonAPI.removeMatchFromRound(
											id,
											round.groupId,
											round.id,
											data.id,
										),
									)
								}
							>
								<LinkOffIcon fontSize="small" />
							</CompetitionAction>
						</>
					) : (
						<>
							<CompetitionAction
								readOnly={readOnly}
								variant="outlined"
								size="small"
								className="cs-icon-action"
								title="Assegna giornata"
								aria-label="Assegna giornata"
								onClick={() => open("assign", {matchId: data.id})}
								disabled={!rounds.length}
							>
								<EventIcon fontSize="small" />
							</CompetitionAction>
						</>
					)}
				</div>
			</div>
		);
	};
	const participantRow = (participant, group) => {
		const p = participants.find((p) => p.id === participant.id) || participant;
		return (
			<div className="cs-team-row" key={p.id}>
				{p.clubId ? (
					<Link
						className="cs-roster-name"
						to={"/settings/clubs/" + p.clubId}
						title={"Apri club " + p.clubName}
					>
						{label(p)}
					</Link>
				) : (
					<span>{label(p)}</span>
				)}
				<div className="cs-actions">
					<CompetitionAction
						readOnly={readOnly}
						variant="outlined"
						size="small"
						className="cs-icon-action"
						title="Modifica iscrizione"
						aria-label="Modifica iscrizione"
						onClick={() =>
							open("participant", {
								id: p.id,
								name: p.name,
								club: {value: p.clubId, label: p.clubName},
								roster: p.rosterId
									? {
											value: p.rosterId,
											label:
												rosters.find((r) => r.id === p.rosterId)?.name ||
												p.name,
										}
									: null,
							})
						}
					>
						<EditIcon fontSize="small" />
					</CompetitionAction>
					<CompetitionAction
						readOnly={readOnly}
						variant="outlined"
						size="small"
						className="cs-icon-action"
						title="Rimuovi iscrizione"
						aria-label="Rimuovi iscrizione"
						color="error"
						onClick={() => open("deleteParticipant", {id: p.id})}
					>
						<DeleteIcon fontSize="small" />
					</CompetitionAction>

					{group ? (
						<CompetitionAction
							readOnly={readOnly}
							variant="outlined"
							size="small"
							className="cs-icon-action"
							title="Rimuovi dal girone"
							aria-label="Rimuovi dal girone"
							onClick={() =>
								action(() =>
									CompetitionSeasonAPI.linkParticipant(
										id,
										group.id,
										p.id,
										true,
									),
								)
							}
						>
							<RemoveIcon fontSize="small" />
						</CompetitionAction>
					) : (
						<CompetitionAction
							readOnly={readOnly}
							variant="outlined"
							size="small"
							className="cs-icon-action"
							title="Assegna al girone"
							aria-label="Assegna al girone"
							disabled={!participantGroups.length}
							onClick={() =>
								open("link", {participant: {value: p.id, label: label(p)}})
							}
						>
							<GroupAddIcon fontSize="small" />
						</CompetitionAction>
					)}
				</div>
			</div>
		);
	};
	const content = {
		info: (
			<section
				className="cs-edition-overview"
				aria-label="Informazioni principali"
			>
				<strong className="cs-edition-overview__name">
					{cs?.competition?.name}
				</strong>
				<div className="cs-edition-overview__field">
					<span>Stagione</span>
					<strong>{cs?.season?.name || "—"}</strong>
				</div>
				<div className="cs-edition-overview__field">
					<span>Categoria</span>
					<strong>{cs?.playerCategory || "Non indicata"}</strong>
				</div>
				<button
					type="button"
					className="cs-edition-overview__details"
					aria-label="Regole e note"
					aria-expanded={showEditionRules}
					aria-controls="edition-rules-content"
					onClick={() => setShowEditionRules((value) => !value)}
				>
					Regole e note
				</button>
				{showEditionRules && (
					<div
						id="edition-rules-content"
						className="cs-edition-overview__detail-content"
					>
						<p>
							<span>Tempi predefiniti</span>
							{sportingSummary(editionSportingDefaults(cs).sportingPeriods)}
						</p>
						<p>
							<span>Rientri predefiniti</span>
							{cs?.defaultAllowReentry ? "Consentiti" : "Non consentiti"}. Si
							applicano alle nuove partite.
						</p>
						{cs?.notes && (
							<p className="cs-edition-overview__notes">
								<span>Note</span>
								{cs.notes}
							</p>
						)}
					</div>
				)}
			</section>
		),
		squadre: (
			<div className="cs-participants">
				<div className="cs-participants__toolbar">
					<div className="cs-actions">
						<CompetitionAction
							readOnly={readOnly}
							variant="outlined"
							size="small"
							startIcon={<GroupAddIcon fontSize="small" />}
							className="cs-add-action"
							title="Iscrivi squadra"
							aria-label="Iscrivi squadra"
							onClick={() => open("participant")}
						>
							Iscrivi squadra
						</CompetitionAction>
						<CompetitionAction
							readOnly={readOnly}
							variant="outlined"
							size="small"
							startIcon={<AddIcon fontSize="small" />}
							className="cs-add-action"
							title="Nuovo girone"
							aria-label="Nuovo girone"
							onClick={() =>
								open("group", {sortOrder: cs?.groups?.length || 0})
							}
						>
							Nuovo girone
						</CompetitionAction>
					</div>
				</div>
				<ParticipantsNavigation
					currentTab={selectedParticipantsTab}
					setCurrentTab={setParticipantsTab}
					tabs={[
						{
							key: "unassigned",
							label: (
								<span>
									Da assegnare{" "}
									<b className="cs-participants__count">
										{unassignedParticipants.length}
									</b>
								</span>
							),
							render: () => (
								<section
									className="cs-participants__panel"
									aria-label="Squadre da assegnare"
								>
									<div className="cs-section-header">
										<strong>Squadre da assegnare</strong>
										<span className="cs-participants__summary">
											{unassignedParticipants.length} squadre
										</span>
									</div>
									<p className="cs-participants__hint">
										Iscritte all'edizione e non ancora associate ad alcun
										girone.
									</p>
									{unassignedParticipants.map((p) => participantRow(p))}
									{!unassignedParticipants.length && (
										<p className="cs-participants__empty">
											{participants.length
												? "Tutte le squadre sono assegnate a un girone."
												: "Nessuna squadra iscritta all'edizione."}
										</p>
									)}
								</section>
							),
						},
						...participantGroups.map((g) => ({
							key: g.id,
							label: (
								<span>
									{g.name}{" "}
									<b className="cs-participants__count">
										{g.participants?.length || 0}
									</b>
								</span>
							),
							render: () => (
								<section className="cs-participants__panel" aria-label={g.name}>
									<div className="cs-section-header">
										<strong>{g.name}</strong>
										<div className="cs-actions">
											<CompetitionAction
												readOnly={readOnly}
												variant="outlined"
												size="small"
												className="cs-icon-action"
												title="Modifica girone"
												aria-label="Modifica girone"
												onClick={() => open("group", g)}
											>
												<EditIcon fontSize="small" />
											</CompetitionAction>
											<CompetitionAction
												readOnly={readOnly}
												variant="outlined"
												size="small"
												className="cs-icon-action"
												title="Elimina girone"
												aria-label="Elimina girone"
												color="error"
												onClick={() => open("deleteGroup", g)}
											>
												<DeleteIcon fontSize="small" />
											</CompetitionAction>
											<CompetitionAction
												readOnly={readOnly}
												variant="outlined"
												size="small"
												startIcon={<GroupAddIcon fontSize="small" />}
												className="cs-add-action"
												title="Aggiungi squadra"
												aria-label="Aggiungi squadra"
												onClick={() => open("link", {groupId: g.id})}
											>
												Aggiungi squadra
											</CompetitionAction>
										</div>
									</div>
									{(g.participants || []).map((p) => participantRow(p, g))}
									{!g.participants?.length && (
										<p className="cs-participants__empty">
											Nessuna squadra assegnata a questo girone.
										</p>
									)}
								</section>
							),
						})),
					]}
				/>
			</div>
		),
		calendario: cs && (
			<CompetitionEditionCalendar
				key={id}
				edition={cs}
				selection={calendarSelection}
				onSelectionChange={setCalendarSelection}
				renderMatch={matchRow}
				renderAddMatch={() => (
					<CompetitionAction
						readOnly={readOnly}
						variant="outlined"
						size="small"
						startIcon={<AddIcon fontSize="small" />}
						className="cs-add-action"
						title="Nuova partita da assegnare"
						aria-label="Nuova partita da assegnare"
						onClick={() =>
							cs?.competition?.competitionType === "Amichevole"
								? navigate("/matches/new", {
										state: {
											matchType: "Amichevole",
											competitionSeasonId: id,
											seasonId: cs.seasonId,
										},
									})
								: open("match", {
										...editionSportingDefaults(cs),
										homeGoals: "",
										awayGoals: "",
									})
						}
						disabled={
							cs?.competition?.competitionType !== "Amichevole" &&
							participants.length < 2
						}
					>
						Nuova partita da assegnare
					</CompetitionAction>
				)}
				renderAddRound={(g) => (
					<CompetitionAction
						readOnly={readOnly}
						variant="outlined"
						size="small"
						startIcon={<AddIcon fontSize="small" />}
						className="cs-add-action"
						title="Aggiungi giornata"
						aria-label="Aggiungi giornata"
						onClick={() =>
							open("round", {
								groupId: g.id,
								roundNumber:
									Math.max(0, ...g.rounds.map((r) => r.roundNumber)) + 1,
							})
						}
					>
						Nuova giornata
					</CompetitionAction>
				)}
				renderRoundActions={(r) => (
					<>
						<CompetitionAction
							readOnly={readOnly}
							variant="outlined"
							size="small"
							className="cs-icon-action"
							title="Modifica giornata"
							aria-label="Modifica giornata"
							onClick={() =>
								open("round", {
									...r,
									startDate: r.startDate?.slice(0, 10) || "",
									endDate: r.endDate?.slice(0, 10) || "",
								})
							}
						>
							<EditIcon fontSize="small" />
						</CompetitionAction>
						<CompetitionAction
							readOnly={readOnly}
							variant="outlined"
							size="small"
							className="cs-icon-action"
							title="Elimina giornata"
							aria-label="Elimina giornata"
							color="error"
							onClick={() => open("deleteRound", r)}
						>
							<DeleteIcon fontSize="small" />
						</CompetitionAction>
						<CompetitionAction
							readOnly={readOnly}
							variant="outlined"
							size="small"
							startIcon={<AddIcon fontSize="small" />}
							className="cs-add-action"
							title="Aggiungi partita"
							aria-label="Aggiungi partita"
							onClick={() =>
								open("match", {
									roundId: r.id,
									...editionSportingDefaults(cs),
									homeGoals: "",
									awayGoals: "",
								})
							}
							disabled={participants.length < 2}
						>
							Nuova partita
						</CompetitionAction>
					</>
				)}
			/>
		),
	};
	const text = (key, label, type = "text") => (
		<VLFTextField
			label={label}
			type={type}
			value={fields[key] ?? ""}
			onChange={(e) => setFields({...fields, [key]: e.target.value})}
		/>
	);
	const select = (key, label, options, onChange) => (
		<VLFSelectNew
			label={label}
			value={fields[key] || null}
			options={options}
			isClearable
			onChange={(value) =>
				onChange ? onChange(value) : setFields({...fields, [key]: value})
			}
		/>
	);
	const pOptions = participants.map((p) => ({value: p.id, label: label(p)}));
	let modalContent = null;
	if (modal?.type === "group")
		modalContent = (
			<>
				{text("name", "Nome")}
				{text("sortOrder", "Ordinamento", "number")}
			</>
		);
	if (modal?.type === "round")
		modalContent = (
			<>
				{text("name", "Nome")}
				{text("roundNumber", "Numero", "number")}
				{text("startDate", "Data inizio", "date")}
				{text("endDate", "Data fine", "date")}
			</>
		);
	if (modal?.type === "participant")
		modalContent = (
			<>
				{select(
					"club",
					"Club",
					clubs.map((c) => ({value: c.id, label: c.name})),
					(club) => setFields({...fields, club, roster: null}),
				)}
				{select(
					"roster",
					fields.club?.value === getInternalClubId()
						? "Rosa (obbligatoria)"
						: "Rosa (facoltativa)",
					rosters
						.filter((r) => r.clubId === fields.club?.value && !r.isPool)
						.map((r) => ({value: r.id, label: r.name})),
					(roster) =>
						setFields({...fields, roster, name: roster?.label || fields.name}),
				)}
				{text("name", "Nome squadra")}
				<p>Il club resta unico; il nome distingue le squadre iscritte.</p>
			</>
		);
	if (modal?.type === "link")
		modalContent = modal.groupId ? (
			select(
				"participant",
				"Squadra iscritta",
				pOptions.filter(
					(p) =>
						!cs.groups
							.find((g) => g.id === fields.groupId)
							?.participants?.some((x) => x.id === p.value),
				),
			)
		) : (
			<>
				<p>{fields.participant?.label}</p>
				{select(
					"group",
					"Girone",
					participantGroups.map((g) => ({value: g.id, label: g.name})),
				)}
			</>
		);
	if (modal?.type === "assign")
		modalContent = select(
			"round",
			"Giornata",
			rounds.map((r) => ({
				value: r.id,
				label: r.groupName + " — " + r.name,
				groupId: r.groupId,
			})),
		);
	if (modal?.type === "match")
		modalContent = (
			<>
				{select("home", "Squadra casa", pOptions)}
				{select("away", "Squadra ospite", pOptions)}
				<VLFDatePicker
					type={fields.startTimeToBeConfirmed ? "date" : "datetime"}
					label={fields.startTimeToBeConfirmed ? "Data" : "Data e orario"}
					value={fields.startDate ? new Date(fields.startDate) : null}
					onChange={(startDate) => setFields((prev) => ({...prev, startDate}))}
					required
					minDate={
						cs?.season?.startDate ? new Date(cs.season.startDate) : undefined
					}
					maxDate={
						cs?.season?.endDate ? new Date(cs.season.endDate) : undefined
					}
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={!!fields.startTimeToBeConfirmed}
							onChange={(e) =>
								setFields((prev) => ({
									...prev,
									startTimeToBeConfirmed: e.target.checked,
								}))
							}
						/>
					}
					label="Orario da definire"
				/>
				{text("duration", "Intervallo calendario (minuti)", "number")}
				<MatchSportingConfig
					periods={fields.sportingPeriods}
					allowReentry={fields.allowReentry}
					locked={sportingStructureLocked(fields.original)}
					onChange={(value) => setFields((prev) => ({...prev, ...value}))}
				/>
				{text("homeGoals", "Gol casa", "number")}
				{text("awayGoals", "Gol ospite", "number")}
			</>
		);
	if (modal?.type?.startsWith("delete"))
		modalContent = (
			<>
				<p>
					{modal.type === "deleteRound"
						? "Eliminando solo la giornata, le partite restano nell'edizione in Partite da assegnare e nel calendario generale."
						: modal.type === "deleteGroup"
							? "Le partite restano nell'edizione in Partite da assegnare."
							: "L'iscrizione può essere eliminata solo se non ha partite."}
				</p>
				{modal.type === "deleteRound" && (
					<FormControlLabel
						control={
							<Checkbox
								checked={Boolean(fields.deleteMatches)}
								onChange={(e) =>
									setFields({...fields, deleteMatches: e.target.checked})
								}
							/>
						}
						label="Elimina anche tutte le partite e i relativi dati (presenze, RPE, valutazioni e osservazioni)"
					/>
				)}
			</>
		);
	const confirmDelete = () =>
		action(() =>
			modal.type === "deleteRound"
				? CompetitionSeasonAPI.deleteRound(
						id,
						modal.groupId,
						modal.id,
						Boolean(fields.deleteMatches),
					)
				: modal.type === "deleteGroup"
					? CompetitionSeasonAPI.deleteGroup(id, modal.id)
					: CompetitionSeasonAPI.deleteParticipant(id, modal.id),
		);
	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={[
					{
						label: "Competizioni",
						path: listPath,
					},
				]}
				current={
					(cs?.competition?.name || "") + " — " + (cs?.season?.name || "")
				}
				actions={
					<MainActions
						actions={[
							{
								icon: <ArrowBackIcon />,
								onClick: () => navigate(listPath),
							},
							{
								icon: <EditIcon />,
								onClick: () =>
									navigate("/settings/competition-seasons/" + id + "/edit"),
							},
						].filter((action, index) => !readOnly || index === 0)}
					/>
				}
			/>
			{cs && (
				<Tabs
					tabs={[
						{
							key: "info",
							label: "Informazioni",
							render: () => (
								<div className="cs-edition-information">
									{content.info}
									{content.calendario}
								</div>
							),
						},
						{
							key: "squadre",
							label: "Partecipanti",
							render: () => content.squadre,
						},
					]}
					freeHeight
					currentTab={tab}
					setCurrentTab={setTab}
				/>
			)}
			<VLFModal
				open={Boolean(modal)}
				title={
					modal?.type?.startsWith("delete")
						? "Conferma eliminazione"
						: modal?.type === "participant"
							? "Iscrivi squadra"
							: modal?.type === "round"
								? "Giornata"
								: modal?.type === "group"
									? "Girone"
									: modal?.type === "match"
										? "Partita"
										: modal?.type === "assign"
											? "Assegna giornata"
											: modal?.type === "link" && !modal.groupId
												? "Assegna al girone"
												: "Aggiungi squadra"
				}
				content={modalContent}
				close={() => setModal(null)}
				confirm={modal?.type?.startsWith("delete") ? confirmDelete : save}
				confirmLabel={
					modal?.type === "deleteRound"
						? fields.deleteMatches
							? "Elimina giornata e partite"
							: "Elimina solo giornata"
						: modal?.type?.startsWith("delete")
							? "Conferma"
							: "Salva"
				}
				disabled={loading}
			/>
		</Page>
	);
}
